/**
 * What the iframe knows about itself before it has spoken to anyone.
 *
 * Exactly three things, and all three arrive in its own URL because the loader
 * put them there: which widget key this is, and how the host page wants the panel
 * to look and speak. Everything else — most importantly the HOST PAGE'S ORIGIN —
 * arrives over the handshake, where the browser vouches for it.
 *
 * That split is the whole point and is worth stating plainly: a value in the URL
 * is asserted by whoever built the URL, and a value in `event.origin` is asserted
 * by the browser. The public key is fine to assert (it is public, and the API
 * still checks it); the origin is not, because the API's `allowed_origins` check
 * is only worth something if the origin it checks is the one the browser saw.
 */
import { toThemePreference, THEME_QUERY_PARAM, type ThemePreference } from '../theme/theme';

export const KEY_QUERY_PARAM = 'k';
export const LOCALE_QUERY_PARAM = 'l';

export interface BootParams {
  readonly publicKey: string;
  readonly theme: ThemePreference;
  /** A requested locale, unvalidated here — `resolveWidgetLocale` narrows it. */
  readonly requestedLocale: string | null;
}

export type BootResult =
  | { readonly ok: true; readonly params: BootParams }
  | { readonly ok: false; readonly reason: 'missing-key' };

/**
 * `search` is passed in rather than read from `window` so this is testable and so
 * there is exactly one place that decides what an absent key means.
 *
 * A missing key is not recoverable and must not be papered over: without it there
 * is no tenant, no brand and no configuration, and any request we made would be
 * a guess. The app renders a quiet failure state instead — quiet because the
 * visitor did not do anything wrong and cannot fix it.
 */
export function readBootParams(search: string): BootResult {
  const params = new URLSearchParams(search);

  const publicKey = params.get(KEY_QUERY_PARAM)?.trim() ?? '';
  if (publicKey === '') {
    return { ok: false, reason: 'missing-key' };
  }

  const locale = params.get(LOCALE_QUERY_PARAM)?.trim() ?? '';

  return {
    ok: true,
    params: {
      publicKey,
      theme: toThemePreference(params.get(THEME_QUERY_PARAM)),
      requestedLocale: locale === '' ? null : locale,
    },
  };
}
